import React, { useReducer, Suspense } from "react";
import { useReducedMotion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  Box,
  Image,
  Flex,
  IconButton,
  Drawer,
  DrawerBody,
  DrawerOverlay,
  DrawerContent,
  useDisclosure,
  SlideFade,
  Fade,
  Spinner,
} from "@chakra-ui/react";
import { HamburgerIcon, CloseIcon } from "@chakra-ui/icons";
import MenuUnderImage from "./images/Background/MenuUnder.png";
import MenuTop from "./images/Menu/MenuTop.png";
import MenuTrailer from "./images/Menu/MenuTtailer.png";
import MenuAbout from "./images/Menu/MenuAbout.png";
import MenuSchedule from "./images/Menu/MenuSchedule.png";
import MenuTicket from "./images/Menu/MenuTicket.png";
import MenuPerformer from "./images/Menu/MenuPerformer.png";
import TGMbanner from "./images/TGMbanner.png";

import Trailer from "./components/Trailer";
import Loading from "./components/Loading";

const Story = React.lazy(() => import("./components/Story"));
const Schedule = React.lazy(() => import("./components/Schedule"));
const Cautions = React.lazy(() => import("./components/Cautions"));

interface MenuItem {
  src: string;
  alt: string;
}

const images = {
  MenuUnderImage,
  MenuTop,
  MenuTrailer,
  MenuAbout,
  MenuSchedule,
  MenuTicket,
  MenuPerformer,
};

const menuItems: MenuItem[] = [
  { src: images.MenuTop, alt: "top" },
  { src: images.MenuTrailer, alt: "trailer" },
  { src: images.MenuAbout, alt: "about" },
  { src: images.MenuSchedule, alt: "schedule" },
  { src: images.MenuTicket, alt: "ticket" },
  { src: images.MenuPerformer, alt: "performer" },
];

/** トレーラー動画を非表示の間は false。再開時に true に戻す */
const IS_TRAILER_MENU_LINK_ENABLED = true;

type ShellState = {
  isLoading: boolean;
  isMenuVisible: boolean;
};

type ShellAction = { type: "finishLoading" } | { type: "showMenu" };

function shellReducer(state: ShellState, action: ShellAction): ShellState {
  switch (action.type) {
    case "finishLoading":
      return state.isLoading ? { ...state, isLoading: false } : state;
    case "showMenu":
      return state.isMenuVisible ? state : { ...state, isMenuVisible: true };
    default:
      return state;
  }
}

/** ロード／メニュー遅延／遅延プリロードを1か所にまとめ、useEffect 内の複数 dispatch を避ける */
function setupAppShellEffects(
  dispatch: React.Dispatch<ShellAction>,
): () => void {
  const finishLoading = () => dispatch({ type: "finishLoading" });

  window.addEventListener("load", finishLoading);
  const loadingTimer = setTimeout(finishLoading, 3000);
  const menuTimer = setTimeout(() => dispatch({ type: "showMenu" }), 1000);

  return () => {
    window.removeEventListener("load", finishLoading);
    clearTimeout(loadingTimer);
    clearTimeout(menuTimer);
  };
}

const colors = {
  brown: "rgb(60, 17, 0)",
};

const App = () => {
  const prefersReducedMotion = useReducedMotion();
  const scrollBehavior: ScrollBehavior = prefersReducedMotion
    ? "auto"
    : "smooth";
  const [shell, dispatchShell] = useReducer(shellReducer, {
    isLoading: true,
    isMenuVisible: false,
  });
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Wait until layout is stable (no size changes) before scrolling
  const waitForLayoutStability = (
    quietMs = 200,
    timeoutMs = 1000,
  ): Promise<void> => {
    return new Promise((resolve) => {
      let lastChange = Date.now();
      const timeout = setTimeout(done, timeoutMs);

      function done() {
        obs.disconnect();
        clearInterval(poll);
        clearTimeout(timeout);
        resolve();
      }

      const obs = new ResizeObserver(() => {
        lastChange = Date.now();
      });
      obs.observe(document.body);

      const poll = setInterval(() => {
        if (Date.now() - lastChange >= quietMs) {
          done();
        }
      }, 50);
    });
  };

  // Ensure target section's images are decoded/loaded before scrolling
  const ensureSectionReady = async (
    id: string,
    timeoutMs = 1800,
  ): Promise<void> => {
    // Try both the anchor and the image itself
    const anchor =
      document.getElementById(id) ||
      (id === "story-image-anchor"
        ? document.getElementById("story-image")
        : id === "schedule-image-anchor"
          ? document.getElementById("schedule-image")
          : id === "ticket-image-anchor"
            ? document.getElementById("ticket-image")
            : null);
    if (!anchor) return;

    const candidates: HTMLImageElement[] = [];
    // 1) Immediate next sibling image
    const nextEl = anchor.nextElementSibling as HTMLElement | null;
    if (nextEl) {
      if (nextEl.tagName.toLowerCase() === "img") {
        candidates.push(nextEl as unknown as HTMLImageElement);
      }
      candidates.push(
        ...(Array.from(nextEl.querySelectorAll("img")) as HTMLImageElement[]),
      );
    }
    // 2) Images within the closest section-like container
    const container = (anchor.closest(
      '[id$="-section"], section, [data-scroll-section]',
    ) || anchor.parentElement) as HTMLElement | null;
    if (container) {
      candidates.push(
        ...(Array.from(
          container.querySelectorAll("img"),
        ) as HTMLImageElement[]),
      );
    }

    // Deduplicate and keep first few
    const imgs = Array.from(new Set(candidates)).slice(0, 6);
    if (imgs.length === 0) return;

    const decodes = imgs.map((img) =>
      (img.complete
        ? Promise.resolve()
        : (img.decode?.() ??
          new Promise<void>((res) =>
            img.addEventListener("load", () => res(), { once: true }),
          ))
      ).catch(() => {}),
    );

    await Promise.race([
      Promise.all(decodes).then(() => undefined),
      new Promise<void>((res) => setTimeout(res, timeoutMs)),
    ]);
  };

  // Compute fixed header offset using responsive breakpoints
  const getHeaderOffset = (): number => {
    const w = window.innerWidth;
    return w >= 1536
      ? 80
      : w >= 1200
        ? 70
        : w >= 992
          ? 70
          : w >= 768
            ? 60
            : 120;
  };

  // Additional downward offset for small screens (base/sm), target-aware for sm
  const getAdditionalOffset = (id: string): number => {
    const w = window.innerWidth;
    if (w < 480) {
      // base: only schedule/performer scroll deeper; others higher (no extra)
      if (id === "performer-section") return 220; // deeper for performer on base
      if (id === "schedule-image-anchor") return 100;
      return 80; // others: lower than before
    }
    if (w < 768) {
      // sm: performer deeper (220), schedule moderate (80), others higher (0)
      if (id === "performer-section") return 220;
      if (id === "schedule-image-anchor") return 80;
      return 80; // others: lower than before
    }
    return 0;
  };

  // Scroll helper: compute exact target top and perform smooth+snap
  const lastTopRef = React.useRef<Record<string, number>>({});
  const scrollToWithOffset = (id: string): void => {
    const element = document.getElementById(id);
    if (!element) return;
    const header = getHeaderOffset();
    const rectTop = element.getBoundingClientRect().top || 0;
    let targetTop = Math.round(
      window.scrollY + rectTop - header + getAdditionalOffset(id),
    );
    const last = lastTopRef.current[id];
    if (typeof last === "number" && Math.abs(last - targetTop) <= 12) {
      targetTop = last; // stick to previous position
    }
    lastTopRef.current[id] = targetTop;
    window.scrollTo({ top: targetTop, behavior: scrollBehavior });
  };

  const handleMenuClick = async (id: string): Promise<void> => {
    await waitForLayoutStability();
    await ensureSectionReady(id);
    scrollToWithOffset(id);
  };

  // Cautionsコンポーネントの表示状態を監視
  const { ref: cautionsRef, inView: cautionsInView } = useInView({
    threshold: 0.1, // 10%表示されたら検出
    triggerOnce: false,
  });

  React.useEffect(() => setupAppShellEffects(dispatchShell), []);

  return (
    <Box w="100%" minH="100vh" onContextMenu={(e) => e.preventDefault()}>
      <Suspense fallback={<Spinner />}>
        {shell.isLoading && <Loading />}
      </Suspense>
      <Box
        id="critical-header"
        bg={{
          base: "transparent",
          md: colors.brown,
        }}
        w="100%"
        pos="relative"
        h={{
          base: "120px",
          sm: "120px",
          md: "60px",
          lg: "70px",
          xl: "70px",
          "2xl": "80px",
        }}
      >
        <Fade
          in={shell.isMenuVisible}
          transition={{ enter: { duration: 0.5 } }}
        >
          <Box
            pos="fixed"
            id="fixed-menu"
            top={{
              base: "20px",
              sm: "20px",
              md: "10px",
              lg: "20px",
            }}
            left={0}
            right={0}
            zIndex={1000}
          >
            {/* モバイル用ハンバーガーメニュー */}
            {!isOpen && (
              <Box
                position="fixed"
                top={0}
                right={0}
                p={4}
                zIndex={20}
                display={{
                  base: "block",
                  sm: "block",
                  md: "none",
                }}
              >
                <IconButton
                  aria-label="Menu"
                  icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
                  onClick={onOpen}
                  bg="black"
                  color="white"
                  _hover={{ bg: "black" }}
                  w="30px"
                  h="30px"
                  fontSize="20px"
                />
              </Box>
            )}

            {/* モバイル用ドロワーメニュー */}
            <Drawer
              isOpen={isOpen}
              placement="right"
              onClose={onClose}
              size="full"
              blockScrollOnMount={false}
            >
              <>
                <DrawerOverlay
                  zIndex={1000}
                  display={{ base: "block", sm: "block", md: "none" }}
                />
                <DrawerContent
                  zIndex={1001}
                  bg={{ base: "transparent", md: colors.brown }}
                  display={{ base: "block", sm: "block", md: "none" }}
                >
                  {/* ドロワーの中に閉じるボタンを配置 */}
                  <Box
                    position="absolute"
                    top={{
                      base: "20px",
                      sm: "20px",
                      md: "10px",
                      lg: "20px",
                    }}
                    right={{
                      base: "20px",
                      sm: "20px",
                      md: "20px",
                      lg: "20px",
                    }}
                    zIndex={1100}
                  >
                    <IconButton
                      icon={<CloseIcon />}
                      onClick={onClose}
                      aria-label="Close menu"
                      bg="black"
                      color="white"
                      _hover={{ bg: "gray.800" }}
                      w={{
                        base: "30px",
                        sm: "30px",
                        md: "45px",
                        lg: "auto",
                      }}
                      h={{
                        base: "30px",
                        sm: "30px",
                        md: "45px",
                        lg: "auto",
                      }}
                      fontSize={{
                        base: "16px",
                        sm: "16px",
                        md: "20px",
                        lg: "24px",
                      }}
                    />
                  </Box>

                  <DrawerBody pt={10}>
                    <Flex direction="column" align="center" gap={8}>
                      {menuItems.map((item, index) => {
                        const trailerMenuDisabled =
                          item.alt === "trailer" &&
                          !IS_TRAILER_MENU_LINK_ENABLED;
                        return (
                          <SlideFade
                            key={item.alt}
                            in={isOpen}
                            transition={{
                              enter: {
                                delay: 0.3 + index * 0.1,
                                duration: 0.4,
                              },
                            }}
                            offsetY="-20px"
                          >
                            <Image
                              src={item.src}
                              alt={item.alt}
                              h="34px"
                              cursor={
                                trailerMenuDisabled ? "not-allowed" : "pointer"
                              }
                              opacity={trailerMenuDisabled ? 0.45 : 1}
                              pointerEvents={
                                trailerMenuDisabled ? "none" : "auto"
                              }
                              aria-disabled={trailerMenuDisabled || undefined}
                              onClick={async () => {
                                if (item.alt === "top") {
                                  await waitForLayoutStability();
                                  window.scrollTo({
                                    top: 0,
                                    behavior: scrollBehavior,
                                  });
                                  return;
                                }
                                if (
                                  item.alt === "trailer" &&
                                  !IS_TRAILER_MENU_LINK_ENABLED
                                )
                                  return;
                                if (item.alt === "trailer")
                                  await handleMenuClick("trailer-video");
                                if (item.alt === "about")
                                  await handleMenuClick("about-title-anchor");
                                if (item.alt === "schedule")
                                  await handleMenuClick(
                                    "schedule-image-anchor",
                                  );
                                if (item.alt === "ticket")
                                  await handleMenuClick("ticket-image-anchor");
                                if (item.alt === "performer")
                                  await handleMenuClick("performer-section");
                                onClose();
                              }}
                            />
                          </SlideFade>
                        );
                      })}
                    </Flex>
                  </DrawerBody>
                </DrawerContent>
              </>
            </Drawer>

            {/* デスクトップ用メニュー用の親Box */}
            <Box
              display={{ base: "none", md: "block" }}
              position="relative"
              h={{
                md: "45px",
                lg: "50px",
                xl: "55px",
                "2xl": "60px",
              }}
              mt={{
                md: "-3px",
                lg: "-10px",
                xl: "-13px",
                "2xl": "-10px",
              }}
            >
              {/* MenuUnderImage */}
              <Box
                w="100%"
                h="100%"
                display={{ base: "none", md: "flex" }}
                justifyContent="center"
                position="relative"
                zIndex={5}
              >
                <Image
                  src={images.MenuUnderImage}
                  alt="menu"
                  display="block"
                  opacity={0.8}
                  w={{
                    md: "65%",
                    lg: "65%",
                    xl: "65%",
                    "2xl": "65%",
                  }}
                  h="100%"
                  maxW={{
                    md: "650px",
                    lg: "800px",
                    xl: "1000px",
                    "2xl": "1200px",
                  }}
                />
              </Box>

              {/* デスクトップ用メニュー */}
              <Flex
                justify="center"
                pos="absolute"
                top="50%"
                left={0}
                right={0}
                transform="translateY(-50%)"
                gap={{
                  base: 14,
                  sm: 14,
                  md: 4,
                  lg: 6,
                  xl: 8,
                  "2xl": 14,
                }}
                zIndex={10}
                display={{ base: "none", md: "flex" }}
              >
                {menuItems.map((item, index) => {
                  const trailerMenuDisabled =
                    item.alt === "trailer" && !IS_TRAILER_MENU_LINK_ENABLED;
                  return (
                    <Image
                      key={item.alt}
                      src={item.src}
                      alt={item.alt}
                      h="auto"
                      maxH={{
                        base: "50px",
                        sm: "50px",
                        md: "18px",
                        lg: "22px",
                        xl: "26px",
                        "2xl": "36px",
                      }}
                      cursor={trailerMenuDisabled ? "not-allowed" : "pointer"}
                      opacity={trailerMenuDisabled ? 0.45 : 1}
                      pointerEvents={trailerMenuDisabled ? "none" : "auto"}
                      aria-disabled={trailerMenuDisabled || undefined}
                      onClick={async () => {
                        if (item.alt === "top") {
                          await waitForLayoutStability();
                          window.scrollTo({
                            top: 0,
                            behavior: scrollBehavior,
                          });
                          return;
                        }
                        if (
                          item.alt === "trailer" &&
                          !IS_TRAILER_MENU_LINK_ENABLED
                        )
                          return;
                        if (item.alt === "trailer")
                          await handleMenuClick("trailer-video");
                        if (item.alt === "about")
                          await handleMenuClick("about-title-anchor");
                        if (item.alt === "schedule")
                          await handleMenuClick("schedule-image-anchor");
                        if (item.alt === "ticket")
                          await handleMenuClick("ticket-image-anchor");
                        if (item.alt === "performer")
                          await handleMenuClick("performer-section");
                      }}
                    />
                  );
                })}
              </Flex>
            </Box>
          </Box>
        </Fade>
        <Box
          pt={{
            base: "0px",
            sm: "0px",
            md: "60px",
            lg: "70px",
            xl: "70px",
            "2xl": "80px",
          }}
          maxW="100vw"
          overflowX="hidden"
          overflowY="hidden"
        >
          <Box
            id="trailer-section"
            scrollMarginTop={{
              base: "140px",
              sm: "140px",
              md: "70px",
              lg: "90px",
              xl: "90px",
              "2xl": "100px",
            }}
          >
            <Trailer />
          </Box>
          <Box
            id="story-section"
            scrollMarginTop={{
              base: "140px",
              sm: "140px",
              md: "70px",
              lg: "90px",
              xl: "90px",
              "2xl": "100px",
            }}
          >
            <Suspense fallback={null}>
              <Story />
            </Suspense>
          </Box>
          <Box
            id="schedule-section"
            scrollMarginTop={{
              base: "140px",
              sm: "140px",
              md: "70px",
              lg: "90px",
              xl: "90px",
              "2xl": "100px",
            }}
          >
            <Suspense fallback={null}>
              <Schedule />
            </Suspense>
          </Box>
          <Box ref={cautionsRef}>
            <Suspense fallback={null}>
              <Cautions />
            </Suspense>
          </Box>
        </Box>
      </Box>

      {/* リンクボタン（非表示） */}
      {false && (
        <Fade
          in={shell.isMenuVisible && !cautionsInView}
          transition={{ enter: { duration: 1.2, delay: 0.8 } }}
        >
          <Box
            as="a"
            href="https://prtimes.jp/main/html/rd/p/000008289.000001355.html"
            target="_blank"
            rel="noopener noreferrer"
            position="fixed"
            bottom={{ base: "20px", sm: "20px", md: "30px", lg: "40px" }}
            left={{ base: "10px", sm: "15px", md: "25px", lg: "40px" }}
            zIndex={2000}
            borderRadius="full"
            transition="all 0.3s ease"
            _hover={{
              opacity: 0.8,
              transform: "scale(1.05)",
            }}
          >
            <picture>
              <source
                type="image/avif"
                srcSet="/optimized/TGMbanner/TGMbanner-120.avif 120w, /optimized/TGMbanner/TGMbanner-160.avif 160w, /optimized/TGMbanner/TGMbanner-200.avif 200w"
                sizes="(min-width: 1200px) 200px, (min-width: 992px) 180px, (min-width: 768px) 160px, 120px"
              />
              <source
                type="image/webp"
                srcSet="/optimized/TGMbanner/TGMbanner-120.webp 120w, /optimized/TGMbanner/TGMbanner-160.webp 160w, /optimized/TGMbanner/TGMbanner-200.webp 200w"
                sizes="(min-width: 1200px) 200px, (min-width: 992px) 180px, (min-width: 768px) 160px, 120px"
              />
              <Image
                src={TGMbanner}
                alt="TGM Banner"
                loading="lazy"
                decoding="async"
                w={{
                  base: "120px",
                  sm: "140px",
                  md: "160px",
                  lg: "180px",
                  xl: "200px",
                }}
                h={{
                  base: "120px",
                  sm: "140px",
                  md: "160px",
                  lg: "180px",
                  xl: "200px",
                }}
                borderRadius="full"
                objectFit="cover"
                boxShadow="0 8px 32px rgba(0, 0, 0, 0.12)"
                border="3px solid rgba(218, 165, 32, 0.3)"
              />
            </picture>
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default App;
